import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Router } from '@angular/router';
import { LoginService } from '@gk/gk-modules';
import {
  Observable,
  catchError,
  combineLatest,
  map,
  of,
  switchMap,
} from 'rxjs';
import { PortalConfigService } from '../../services/portal-config/portal-config.service';
import { SessionService } from '../../services/session/session.service';
import { MainRoutes, PzInitRoutes } from '../guard.models';

@Injectable()
export class SessionGuard {
  constructor(
    private sessionService: SessionService,
    private router: Router,
    private loginService: LoginService,
    private portalConfigService: PortalConfigService,
  ) {}

  private readonly routeMapping: { [key in MainRoutes]?: PzInitRoutes } = {
    [MainRoutes.Designer]: PzInitRoutes.Designer,
    [MainRoutes.Przp]: PzInitRoutes.Przp,
    [MainRoutes.GrantingRights]: PzInitRoutes.GrantingRights,
    [MainRoutes.RoadExitPortal]: PzInitRoutes.RoadExit,
    [MainRoutes.ExcludingLandFromAgriculturalProductionPortalAgriculturalLandUse]:
      PzInitRoutes.ExcludingLandFromAgriculturalProductionPortalAgriculturalLandUse,
    [MainRoutes.ExcludingLandFromAgriculturalProductionPortalNonAgriculturalLandUse]:
      PzInitRoutes.ExcludingLandFromAgriculturalProductionPortalNonAgriculturalLandUse,
    [MainRoutes.LaneOccupancyPortalInfrastructureDevices]:
      PzInitRoutes.LaneOccupancyInfrastructureDevices,
    [MainRoutes.LaneOccupancyPortalBuildingObjects]:
      PzInitRoutes.LaneOccupancyBuildingObjects,
    [MainRoutes.LaneOccupancyPortalBuildingWorks]:
      PzInitRoutes.LaneOccupancyBuildingWorks,
    [MainRoutes.LandAndBuildingRegisterDataChangesNotificationRequest]:
      PzInitRoutes.LandAndBuildingRegisterDataChangesNotificationRequest,
    [MainRoutes.LandAndBuildingRegisterDataUpdateRequest]:
      PzInitRoutes.LandAndBuildingRegisterDataUpdateRequest,
    [MainRoutes.LandAndBuildingRegisterDataUpdateUponEntitiesRequest]:
      PzInitRoutes.LandAndBuildingRegisterDataUpdateUponEntitiesRequest,
    [MainRoutes.SoilClassificationOfLandRequest]:
      PzInitRoutes.SoilClassificationOfLandRequest,
    [MainRoutes.PresentationCommentsLandBuildingRegistryProject]:
      PzInitRoutes.PresentationCommentsLandBuildingRegistryProject,
    [MainRoutes.AccessToInformationOnEnvironmentAndItsProtection]:
      PzInitRoutes.AccessToInformationOnEnvironmentAndItsProtection,
    [MainRoutes.BuildingPermit]: PzInitRoutes.BuildingPermit,
    [MainRoutes.DemolitionPermit]: PzInitRoutes.DemolitionPermit,
    [MainRoutes.BuildingOrPartUseChange]: PzInitRoutes.BuildingOrPartUseChange,
    [MainRoutes.TemporaryStructureBuildingPermit]:
      PzInitRoutes.TemporaryStructureBuildingPermit,
    [MainRoutes.BuildingPermitModificationDecision]:
      PzInitRoutes.BuildingPermitModificationDecision,
    [MainRoutes.NotObjectedNotificationRightsObligationsTransfer]:
      PzInitRoutes.NotObjectedNotificationRightsObligationsTransfer,
    [MainRoutes.PlotLandDevArchConstrProjSeparateDecision]:
      PzInitRoutes.PlotLandDevArchConstrProjSeparateDecision,
    [MainRoutes.AdjacentPropertyNeedEnterDecision]:
      PzInitRoutes.AdjacentPropertyNeedEnterDecision,
    [MainRoutes.EnvironmentalProtectionPortalFishingCardRequest]:
      PzInitRoutes.EnvironmentalProtectionPortalFishingCardRequest,
    [MainRoutes.ConstructionPortalConstructionNotificationRequest]:
      PzInitRoutes.ConstructionPortalConstructionNotificationRequest,
    [MainRoutes.ConstructionPortalDemolitionNotificationRequest]:
      PzInitRoutes.ConstructionPortalDemolitionNotificationRequest,
    [MainRoutes.ConstructionPortalPremiseIndependenceRequest]:
      PzInitRoutes.ConstructionPortalPremiseIndependenceRequest,
    [MainRoutes.ConstructionPortalConstructionLogRequest]:
      PzInitRoutes.ConstructionPortalConstructionLogRequest,
    [MainRoutes.EnvironmentalProtectionPortalSimplifiedForestManagementPlanRequest]:
      PzInitRoutes.EnvironmentalProtectionPortalSimplifiedForestManagementPlanRequest,
    [MainRoutes.EnvironmentalProtectionPortalTreesAndShrubsFellingRequest]:
      PzInitRoutes.EnvironmentalProtectionPortalTreesAndShrubsFellingRequest,
    [MainRoutes.EnvironmentalProtectionPortalWaterEquipmentRequest]:
      PzInitRoutes.EnvironmentalProtectionPortalWaterEquipmentRequest,
    [MainRoutes.BuildingPermitTransfer]: PzInitRoutes.BuildingPermitTransfer,
    [MainRoutes.PlanningDepartmentExtractFromLocalSpatialDevelopmentPlan]:
      PzInitRoutes.PlanningDepartmentExtractFromLocalSpatialDevelopmentPlanRequest,
    [MainRoutes.PlanningDepartmentCertificateFromLocalSpatialDevelopmentPlan]:
      PzInitRoutes.PlanningDepartmentCertificateFromLocalSpatialDevelopmentPlanRequest,
    [MainRoutes.PlanningDepartmentExtractFromStudy]:
      PzInitRoutes.PlanningDepartmentExtractFromStudy,
    [MainRoutes.SurveyorPortal]: PzInitRoutes.PzInitSystemUrl,
    [MainRoutes.AppraiserPortal]: PzInitRoutes.PzInitSystemUrl,
    [MainRoutes.EwidPro]: PzInitRoutes.PzInitSystemUrl,
    [MainRoutes.AddressNumberRequestPortal]:
      PzInitRoutes.AddressNumberRequestPortal,
    [MainRoutes.OwnershipConversion]: PzInitRoutes.OwnershipConversion,
    [MainRoutes.OwnershipConversionFeeCalculationRequest]:
      PzInitRoutes.OwnershipConversionFeeCalculationRequest,
    [MainRoutes.TreesAndShrubsFallNotification]:
      PzInitRoutes.TreesAndShrubsFallNotification,
    [MainRoutes.TreesAndShrubsFellingWithDecisionRequest]:
      PzInitRoutes.TreesAndShrubsFellingWithDecisionRequest,
    [MainRoutes.TreesAndShrubsRemovalNotification]:
      PzInitRoutes.TreesAndShrubsRemovalNotification,
    [MainRoutes.MonumentConservationGrantRequest]:
      PzInitRoutes.MonumentConservationGrantRequest,
    [MainRoutes.MonumentInstallationRequest]:
      PzInitRoutes.MonumentInstallationRequest,
    [MainRoutes.PropertyOccupancyTechnicalInstallationConsent]:
      PzInitRoutes.PropertyOccupancyTechnicalInstallationConsent,
    [MainRoutes.PropertyLease]: PzInitRoutes.PropertyLease,
    [MainRoutes.SupplementaryParcelPurchase]:
      PzInitRoutes.SupplementaryParcelPurchase,
    [MainRoutes.StreetNaming]: PzInitRoutes.StreetNaming,
    [MainRoutes.CommunalPremisePurchase]: PzInitRoutes.CommunalPremisePurchase,
    [MainRoutes.SingleFamilyBuilding70ConstructionConversion]:
      PzInitRoutes.SingleFamilyBuilding70ConstructionConversion,
    [MainRoutes.SingleFamilyBuildingConstructionConversion]:
      PzInitRoutes.SingleFamilyBuildingConstructionConversion,
    [MainRoutes.GeodeticAndCartographicServiceDataRequest]:
      PzInitRoutes.GeodeticAndCartographicServiceDataRequest,
    [MainRoutes.PermanentManagementFeeDiscountDecisionGrantingRequest]:
      PzInitRoutes.PermanentManagementFeeDiscountDecisionGrantingRequest,
    [MainRoutes.PlanningActCommentsRequest]:
      PzInitRoutes.PlanningActCommentsRequest,
    [MainRoutes.PlanningActCreationAmendmentRequest]:
      PzInitRoutes.PlanningActCreationAmendmentRequest,
    [MainRoutes.PlanningActProjectRequest]:
      PzInitRoutes.PlanningActProjectRequest,
    [MainRoutes.InstallationNotificationWithoutEmissionPermit]:
      PzInitRoutes.InstallationNotificationWithoutEmissionPermit,
    [MainRoutes.HouseholdSewageTreatmentNotification]:
      PzInitRoutes.HouseholdSewageTreatmentNotification,
  };

  authenticate(mainRoute: MainRoutes, queryParams: Params = {}): void {
    const currentPzInitUrl = this.routeMapping[mainRoute];

    if (currentPzInitUrl) {
      this.loginService.navigateToExternalLogin(currentPzInitUrl, queryParams);
    } else {
      this.navigateToNotFound();
    }
  }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const fullPath = route.pathFromRoot
      .flatMap((r) => r.url)
      .map((s) => s.path)
      .filter(Boolean);

    const mainRoute = fullPath[0] as MainRoutes;
    return combineLatest([
      this.sessionService.getSystemSession(),
      this.portalConfigService.getPortalConfByMainRoute(mainRoute),
    ]).pipe(
      switchMap(([sessionState, portalConfig]) =>
        sessionState.isPzAuthenticated()
          ? this.sessionService.isPzSessionCorrectForRoute(mainRoute).pipe(
              map((isPzSessionCorrectForRoute) => ({
                sessionState,
                portalConfig,
                isPzSessionCorrectForRoute,
              })),
            )
          : of({
              sessionState,
              portalConfig,
              isPzSessionCorrectForRoute: false,
            }),
      ),
      map(({ sessionState, portalConfig, isPzSessionCorrectForRoute }) => {
        if (
          portalConfig?.isPerunAuthenticationAvailable &&
          !sessionState.isAuthenticated()
        ) {
          this.navigateToSignInPage(mainRoute);
          return false;
        }

        if (
          (mainRoute === MainRoutes.Designer ||
            portalConfig?.isPerunAuthenticationAvailable) &&
          sessionState.isPerunAuthenticated()
        ) {
          return true;
        }

        if (isPzSessionCorrectForRoute) {
          return true;
        }

        this.authenticate(mainRoute, route.queryParams);
        return false;
      }),
      catchError(() => {
        this.navigateToNotFound();
        return of(false);
      }),
    );
  }

  navigateToSignInPage(portalRoute: MainRoutes): void {
    this.router.navigateByUrl(`/${portalRoute}/${MainRoutes.SignIn}`);
  }

  navigateToNotFound(): void {
    this.router.navigateByUrl(`/${MainRoutes.NotFound}`);
  }
}
